import { useEffect, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../components/ui/hover-card"
import { cn } from "../lib/utils"

export function HoverCardRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain.
  const atomDefaults = useAtomDefaults("hover-card")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const children = element.children ?? []
  const trigger = children[0]
  const content = children[1]

  // defaultOpen: force-open uncontrolled state (preview / initial-open use cases).
  // Matches the pattern already established on alert-dialog-renderer.tsx,
  // dialog-renderer.tsx, popover-renderer.tsx, and tooltip-renderer.tsx.
  // base-ui's PreviewCardRoot supports `defaultOpen` natively.
  const rawOpen = (element as unknown as { open?: unknown }).open
    ?? (element as unknown as { defaultOpen?: unknown }).defaultOpen
  const resolvedOpen = typeof rawOpen === "string" ? resolveSymbol(rawOpen) : rawOpen
  const requestedOpen = resolvedOpen === true || resolvedOpen === "true"
  const [isOpen, setIsOpen] = useState(requestedOpen)

  useEffect(() => {
    if (rawOpen !== undefined) setIsOpen(requestedOpen)
  }, [rawOpen, requestedOpen])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    dispatchEvent(element.id, open ? "open" : "close", {})
  }

  return (
    <span
      data-atom-type="hover-card"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-is-open={isOpen ? "true" : "false"}
      data-testid={`hover-card-wrapper-${element.id}`}
      aria-expanded={isOpen}
      className={cn("inline-block", element.className)}
    >
      <HoverCard open={isOpen} onOpenChange={handleOpenChange}>
        {trigger && (
          <HoverCardTrigger
            data-testid={`hover-card-${element.id}`}
            render={<span />}
          >
            <ElementRenderer element={trigger} />
          </HoverCardTrigger>
        )}
        {content && (
          <HoverCardContent>
            <ElementRenderer element={content} />
          </HoverCardContent>
        )}
      </HoverCard>
    </span>
  )
}
